<template>
    <!-- Global toast layer. Lives once in App.vue; the container ignores pointer
         events so it never blocks the UI behind it, while each toast re-enables
         them for its own buttons.

         Each toast sits in an absolutely-positioned slot translated to its place
         in the stack (translateY). When a toast is dismissed, the survivors'
         offsets recompute and every slot glides to its new spot with ONE
         transform transition — GPU, single smooth motion. This is why the toast
         below no longer steps up in two phases: nothing moves by layout reflow.
         Each leaving toast keeps its own slot, so several can leave at once
         without piling onto the same position. -->
    <TransitionGroup ref="stack" tag="div" :css="false" class="toast-layer"
        @enter="OnEnter" @leave="OnLeave">
        <div v-for="toast in store.toasts" :key="toast.id" class="toast-slot" :data-id="toast.id" :style="SlotStyle(toast.id)">
            <ToastItem :toast="toast" @dismiss="store.Dismiss" @measure="OnMeasure" />
        </div>
    </TransitionGroup>
</template>

<script>
import { defineComponent } from 'vue';
import { useToastStore } from '@/store/ToastStore.js';
import ToastItem from '@/components/Generic/ToastItem.vue';

const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
const GAP = 10;          // vertical space between stacked toasts
const ENTER_MS = 360;
const LEAVE_MS = 260;

export default defineComponent({
    name: 'ToastLayer',

    components: {
        ToastItem
    },

    data() {
        return {
            // id -> measured height (px), reported by each ToastItem.
            heights: {}
        };
    },

    setup() {
        const store = useToastStore();
        return { store };
    },

    watch: {
        'store.toasts': {
            handler() {
                this.PruneHeights();
                this.ReflowStack();
            },
            deep: true
        },

        // A toast reporting (or re-reporting) its height changes the offsets of
        // everything below it — glide the survivors to their new spots.
        heights: {
            handler() {
                this.ReflowStack();
            },
            deep: true
        }
    },

    methods: {
        // Drops heights left over from toasts that are no longer in the store
        // (e.g. dismissed) so the map doesn't grow for the life of the session.
        PruneHeights() {
            const ids = new Set(this.store.toasts.map(toast => toast.id));
            const pruned = {};
            for (const key of Object.keys(this.heights)) {
                if (ids.has(Number(key))) {
                    pruned[key] = this.heights[key];
                }
            }
            this.heights = pruned;
        },

        ReducedMotion() {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        },

        OnMeasure(id, height) {
            // Reassign so the change is reactive and offsets recompute.
            this.heights = { ...this.heights, [id]: height };
        },

        // Vertical offset of a toast = the stacked height of everything above it
        // (earlier in the list; index 0 is the newest, at the top).
        OffsetFor(id) {
            let y = 0;
            for (const toast of this.store.toasts) {
                if (toast.id === id) {
                    break;
                }
                y += (this.heights[toast.id] || 0) + GAP;
            }
            return y;
        },

        // Dynamic per-slot transform (allowed inline exception — runtime value).
        // This is the RESTING position: the slot always renders parked at its
        // target offset, so with no JS (or reduced motion) the stack is correct
        // instantly. ReflowStack layers a WAAPI glide from the old spot to this
        // one on top of it when the stack shifts.
        SlotStyle(id) {
            return { transform: `translateY(${this.OffsetFor(id)}px)` };
        },

        // Current visual Y of a slot, reading through any in-flight glide so an
        // interrupted animation continues smoothly from where it is.
        CurrentY(slotEl) {
            const t = getComputedStyle(slotEl).transform;
            if (!t || t === 'none') {
                return 0;
            }
            try {
                return new DOMMatrixReadOnly(t).m42;
            } catch (e) {
                return 0;
            }
        },

        // Glides each slot from where it currently sits to its new resting
        // offset. Driven by WAAPI instead of a CSS transition on the slot: a
        // transform transition here stalls on mobile Chromium when a slot is
        // repositioned in the same frame a sibling runs its enter animation
        // (the transition reports `running` but its clock stays pinned at 0),
        // which left freshly-arrived toasts piled at offset 0 until one was
        // dismissed. WAAPI (the same mechanism as OnEnter/OnLeave) is reliable.
        ReflowStack() {
            this.$nextTick(() => {
                // The layer's own leading comment makes this component's root a
                // fragment, so this.$el is a text anchor, not the container —
                // reach the real <div class="toast-layer"> through the group ref.
                const container = this.$refs.stack && this.$refs.stack.$el;
                if (!container || typeof container.querySelectorAll !== 'function') {
                    return;
                }

                const live = new Set(this.store.toasts.map(toast => toast.id));
                const slots = container.querySelectorAll('.toast-slot');

                for (const slotEl of slots) {
                    const id = Number(slotEl.dataset.id);
                    // Skip leaving slots — OnLeave owns their motion, and they
                    // are no longer in the stack to be offset.
                    if (!live.has(id)) {
                        continue;
                    }

                    const target = this.OffsetFor(id);

                    if (this.ReducedMotion()) {
                        continue; // resting inline transform already parks it
                    }

                    const current = this.CurrentY(slotEl);
                    if (Math.abs(current - target) < 0.5) {
                        continue; // already there (e.g. a just-entered toast)
                    }

                    if (slotEl._reflow) {
                        slotEl._reflow.cancel();
                    }
                    slotEl._reflow = slotEl.animate([
                        { transform: `translateY(${current}px)` },
                        { transform: `translateY(${target}px)` }
                    ], { duration: 320, easing: EASE });
                }
            });
        },

        Inner(slotEl) {
            return slotEl.querySelector('.toast');
        },

        OnEnter(slotEl, done) {
            const inner = this.Inner(slotEl);

            if (this.ReducedMotion()) {
                const fade = inner.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease', fill: 'forwards' });
                fade.onfinish = () => done();
                return;
            }

            // Slide the card in from the right and fade up. The slot itself is
            // already parked at the correct offset, so only the card moves.
            const anim = inner.animate([
                { opacity: 0, transform: 'translateX(calc(100% + 16px))' },
                { opacity: 1, transform: 'translateX(0) scale(1)' }
            ], { duration: ENTER_MS, easing: EASE, fill: 'backwards' });

            anim.onfinish = () => done();
        },

        OnLeave(slotEl, done) {
            const inner = this.Inner(slotEl);

            // A swipe already flung the card out (inline styles on the item); the
            // survivors are gliding via their own slot transitions. Nothing left
            // to animate here — just retire the slot.
            if (inner.dataset.swiping === 'true') {
                done();
                return;
            }

            if (this.ReducedMotion()) {
                const fade = inner.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, easing: 'ease', fill: 'forwards' });
                fade.onfinish = () => done();
                return;
            }

            const anim = inner.animate([
                { opacity: 1, transform: 'translateX(0) scale(1)' },
                { opacity: 0, transform: 'translateX(36px) scale(0.94)' }
            ], { duration: LEAVE_MS, easing: EASE, fill: 'forwards' });

            anim.onfinish = () => done();
        }
    }
});
</script>

<style scoped>
.toast-layer {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 100000;
    pointer-events: none;
}

/* Absolutely stacked; translateY (set inline as the resting offset) places
   each in the column. The glide between resting spots when the stack shifts is
   run from JS via WAAPI (ReflowStack), NOT a CSS transition here: a transform
   transition on this element stalls on mobile Chromium when a slot is
   repositioned in the same frame a sibling runs its WAAPI enter animation —
   the transition reports `running` but its clock stays pinned at 0, which left
   freshly-arrived toasts piled at offset 0 until one was dismissed. */
.toast-slot {
    position: absolute;
    top: 0;
    right: 0;
}

@media (max-width: 600px) {
    .toast-layer {
        top: 12px;
        right: 12px;
        left: 12px;
    }

    .toast-slot {
        left: 0;
        right: 0;
    }
}
</style>
